import React, { useMemo, useState } from "react";
import {
  Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, TextField, InputAdornment, Box, CircularProgress,
  Typography, Select, MenuItem, Button, Tooltip,
  TablePagination, Checkbox, Chip, Collapse,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import PlaylistAddRoundedIcon from "@mui/icons-material/PlaylistAddRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import GetApp from "@mui/icons-material/GetApp";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { CSVLink } from "react-csv";
import { buildAwarenessGroupTree } from "../utils/awarenessGroupTree";

const AwarenessStageTable = ({
  groups = [],
  rowCount = 0,
  page = 0,                 // zero-based
  pageSize = 50,
  onPageChange,
  onPageSizeChange,
  onSearch,
  isLoading = false,
  onAddChild,
  onEdit,
  onDelete,
  onToggleStatus,
  // bulk
  onBulkActivate,
  onBulkDeactivate,
  onBulkDelete,
}) => {
  const [localSearch, setLocalSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set()); // guarda ids expandidos

  const tree = useMemo(() => buildAwarenessGroupTree(groups), [groups]);

  const toggleExpand = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const isRowSelected = (id) => selected.has(id);
  const toggleSelect = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const allIdsOnPage = useMemo(() => groups.map((g) => g.id), [groups]);
  const allOnPageSelected = groups.length > 0 && groups.every((g) => selected.has(g.id));
  const toggleSelectAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) allIdsOnPage.forEach((id) => next.delete(id));
      else allIdsOnPage.forEach((id) => next.add(id));
      return next;
    });

  const selectedArr = Array.from(selected);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  const exportColumns = [
    { field: "ordem", headerName: "Ordem" },
    { field: "nome", headerName: "Nome" },
    { field: "subetapas", headerName: "Subetapas" },
  ];
  const selectedData = useMemo(
    () =>
      groups
        .filter((g) => selected.has(g.id))
        .map((g) => ({
          ordem: String(g.ordem ?? ""),
          nome: g.nome ?? "—",
          subetapas: String(g.children?.length ?? 0),
        })),
    [groups, selected]
  );
  const exportPDF = () => {
    const doc = new jsPDF();
    doc.text("Etapas de Níveis de Consciência Selecionadas", 20, 10);
    const headers = exportColumns.map((c) => c.headerName);
    const body = selectedData.map((row) => exportColumns.map((col) => row[col.field]));
    doc.autoTable({ head: [headers], body });
    doc.save("etapas_niveis_consciencia.pdf");
  };
  const csvData = useMemo(
    () =>
      selectedData.map((row) => {
        const o = {};
        exportColumns.forEach((c) => (o[c.headerName] = row[c.field]));
        return o;
      }),
    [selectedData]
  );

  const Row = ({ node, depth = 0 }) => {
    const hasChildren = (node.children || []).length > 0;
    const open = expanded.has(node.id);
    const checked = isRowSelected(node.id);
    return (
      <>
        <TableRow hover selected={checked}>
          <TableCell>
            <Checkbox checked={checked} onChange={() => toggleSelect(node.id)} />
          </TableCell>

          <TableCell width={48}>
            {hasChildren && (
              <IconButton size="small" onClick={() => toggleExpand(node.id)}>
                {open ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
              </IconButton>
            )}
          </TableCell>

          <TableCell width={72}>{node.ordem ?? ""}</TableCell>

          <TableCell>
            <Box sx={{ pl: depth * 2 }}>
              {node.nome}
            </Box>
          </TableCell>

          {/* <TableCell>
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <Switch size="small" checked={ativo} onChange={() => onToggleStatus?.(node)} />
              }
              label={ativo ? "Ativa" : "Inativa"}
            />
          </TableCell> */}

          <TableCell>
            <Chip size="small" label={`${node.children?.length || 0} subetapa(s)`} />
          </TableCell>

          <TableCell align="right">
            <Tooltip title="Adicionar subetapa">
              <IconButton size="small" onClick={() => onAddChild?.(node)}>
                <PlaylistAddRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Editar">
              <IconButton size="small" onClick={() => onEdit?.(node)}>
                <EditRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Excluir">
              <IconButton size="small" onClick={() => onDelete?.(node.id)}>
                <DeleteOutlineRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </TableCell>
        </TableRow>

        {hasChildren && (
          <TableRow>
            <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
              <Collapse in={open} timeout="auto" unmountOnExit>
                <Box sx={{ px: 2, py: 1, bgcolor: "action.hover" }}>
                  <Table size="small" sx={{ background: "transparent" }}>
                    <TableHead>
                      <TableRow>
                        <TableCell width={48} />
                        <TableCell width={48} />
                        <TableCell width={72}>Ordem</TableCell>
                        <TableCell>Nome</TableCell>
                        {/* <TableCell>Status</TableCell> */}
                        <TableCell>Subetapas</TableCell>
                        <TableCell align="right">Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {node.children
                        .sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
                        .map((ch) => (
                          <Row key={ch.id} node={ch} depth={depth + 1} />
                        ))}
                    </TableBody>
                  </Table>
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>
        )}
      </>
    );
  };

  return (
    <Paper variant="outlined">
      {/* Busca */}
      <Box sx={{ p: 1.5 }}>
        <TextField
          fullWidth
          placeholder="Buscar"
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onSearch?.(localSearch)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchRoundedIcon /></InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Ações em massa padronizadas */}
      {selectedArr.length > 0 && (
        <Box sx={{ mx: 1.5, mb: 2, py: 1, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <Typography variant="subtitle1" color="text.secondary">
            {selectedArr.length} registro(s) selecionado(s)
          </Typography>
          <Button variant="contained" color="primary" startIcon={<GetApp />} onClick={exportPDF} sx={{ textTransform: "uppercase" }}>
            Exportar PDF
          </Button>
          <Button variant="contained" color="primary" startIcon={<GetApp />} sx={{ textTransform: "uppercase" }}>
            <CSVLink data={csvData} filename="etapas_niveis_consciencia.csv" style={{ textDecoration: "none", color: "inherit" }}>
              Exportar CSV
            </CSVLink>
          </Button>
          <Button
            color="error"
            variant="outlined"
            startIcon={<DeleteOutlineRoundedIcon />}
            onClick={() => setConfirmBulkDelete(true)}
            sx={{ textTransform: "uppercase" }}
          >
            EXCLUIR SELECIONADOS ({selectedArr.length})
          </Button>
        </Box>
      )}

      <Dialog open={confirmBulkDelete} onClose={() => setConfirmBulkDelete(false)}>
        <DialogTitle>Excluir selecionados</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir {selectedArr.length} registro(s) selecionado(s)?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmBulkDelete(false)}>Cancelar</Button>
          <Button
            color="error"
            onClick={() => {
              onBulkDelete?.(selectedArr);
              setConfirmBulkDelete(false);
            }}
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={48}>
                <Checkbox
                  checked={allOnPageSelected}
                  indeterminate={!allOnPageSelected && selectedArr.length > 0}
                  onChange={toggleSelectAll}
                />
              </TableCell>
              <TableCell width={48} />
              <TableCell width={72}>Ordem</TableCell>
              <TableCell>Nome</TableCell>
              {/* <TableCell>Status</TableCell> */}
              <TableCell>Subetapas</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Box sx={{ py: 3, display: "flex", gap: 1, alignItems: "center", justifyContent: "center" }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2">Carregando…</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : tree.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Box sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      Nenhuma etapa encontrada.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              tree.map((n) => <Row key={n.id} node={n} depth={0} />)
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginação */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">Linhas por página:</Typography>
          <Select
            size="small"
            value={pageSize}
            onChange={(e) => onPageSizeChange?.(Number(e.target.value))}
          >
            {[10, 25, 50, 100].map((sz) => (
              <MenuItem key={sz} value={sz}>{sz}</MenuItem>
            ))}
          </Select>
        </Box>

        <TablePagination
          component="div"
          count={rowCount}
          page={page}
          rowsPerPage={pageSize}
          onPageChange={(_, newPage) => onPageChange?.(newPage)}
          onRowsPerPageChange={(e) => onPageSizeChange?.(parseInt(e.target.value, 10))}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Box>
    </Paper>
  );
};

export default AwarenessStageTable;
